import { useState } from 'react';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';
import axiosInstance from '@/lib/axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Trash2, Loader2, UserPlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import type { Member } from '@/types';

interface EventAdmin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  image?: string;
  createdAt: string;
}

interface EventAdminsManagerProps {
  eventId: string;
  communityId: string;
}

export default function EventAdminsManager({ eventId, communityId }: EventAdminsManagerProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [debouncedSearch] = useDebounce(search, 300);
  const shouldFetchMembers = open && debouncedSearch.length >= 2;

  const {
    data: adminsData,
    mutate: mutateAdmins,
    isLoading: isLoadingAdmins,
  } = useSWR(eventId ? `/api/events/${eventId}/admins` : null, (url: string) =>
    axiosInstance.get(url).then((res) => res.data)
  );

  const { data: membersData, isLoading: isLoadingMembers } = useSWR(
    shouldFetchMembers
      ? `/api/communities/${communityId}/members?search=${debouncedSearch}&limit=10`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const admins: EventAdmin[] = adminsData?.admins || [];
  const members: Member[] = membersData?.members || [];

  const handleAddAdmin = async (userId: string) => {
    try {
      setIsAdding(true);
      await axiosInstance.post(`/api/events/${eventId}/admins`, { userId });
      toast.success('Admin added successfully');
      mutateAdmins();
      setOpen(false);
      setSearch('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to add admin');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    try {
      await axiosInstance.delete(`/api/events/${eventId}/admins/${userId}`);
      toast.success('Admin removed successfully');
      mutateAdmins();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to remove admin');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Event Admins</h3>
          <p className="text-sm text-muted-foreground">
            Manage members who have administrative access to this event.
          </p>
        </div>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="default">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Admin
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search community members..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                {debouncedSearch.length < 2 && search.length > 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Type at least 2 characters to search...
                  </div>
                ) : (
                  <CommandEmpty>
                    {isLoadingMembers ? (
                      <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Searching...
                      </div>
                    ) : debouncedSearch.length >= 2 ? (
                      'No members found.'
                    ) : (
                      'Type to search community members...'
                    )}
                  </CommandEmpty>
                )}
                <CommandGroup>
                  {members.map((member) => {
                    const isAlreadyAdmin = admins.some((a) => a.id === member.id);
                    return (
                      <CommandItem
                        key={member.id}
                        value={member.id}
                        onSelect={() => !isAlreadyAdmin && handleAddAdmin(member.id)}
                        disabled={isAlreadyAdmin || isAdding}
                        className="flex items-center justify-between py-2"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={member.image} />
                            <AvatarFallback>{member.name[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{member.name}</span>
                            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {member.email}
                            </span>
                          </div>
                        </div>
                        {isAlreadyAdmin && (
                          <Badge variant="outline" className="text-[10px] px-1 h-4">
                            Admin
                          </Badge>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Admin</TableHead>
              <TableHead>Added On</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingAdmins ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  <div className="flex items-center justify-center italic text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Loading admins...
                  </div>
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                  No additional admins assigned to this event.
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={admin.image} />
                        <AvatarFallback>
                          {admin.firstName?.[0]}
                          {admin.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {admin.firstName} {admin.lastName}
                        </span>
                        <span className="text-xs text-muted-foreground">{admin.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(admin.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveAdmin(admin.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
